import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useClickOutsideAndEscape } from 'hooks'
import DropdownButton from './dropdownButton'
import { DropdownInput, InputOptions } from 'types/input'
import { FieldValues, Path, PathValue } from 'react-hook-form'

/**
 *
 * Custom Dropdown element
 * -
 * Need To Pass Following Params
 * - `name` _required_
 * - `options` _required_
 * - `setValue` _required_
 * - `parentClassName` _optional_
 * - `error` _optional_
 * - `className` _optional_
 * - `hintText` _optional_
 * - `label` _Optional_
 * - `defaultValue` _Optional_
 * - `searchable` _Optional_ - Enable search functionality
 */
const Dropdown = <T extends FieldValues>({
  parentClassName = '',
  className,
  name,
  label,
  defaultValue = undefined,
  setValue,
  hintText,
  options,
  error,
  required = false,
  size = 'medium',
  searchable = true,
  onChange,
}: DropdownInput<T> & { searchable?: boolean }) => {
  // To toggle the dropdown visibility
  const [isVisible, setIsVisible] = useState<boolean>(false)

  // Search state
  const [searchQuery, setSearchQuery] = useState<string>('')
  const searchInputRef = useRef<HTMLInputElement | null>(null)

  // Track whether the user has manually selected an option so the
  // defaultValue effect does not overwrite their choice on re-renders
  const userSelectedRef = useRef<boolean>(false)

  const resolveOption = useCallback(
    (val: unknown): InputOptions | undefined => {
      if (val === undefined) return options[0]
      const matched = options.find((item: InputOptions) => item.value === val)
      return matched !== undefined ? matched : options[0]
    },
    [options],
  )

  // To handle the selected option, if multiselect is true then count will be displayed instead of object
  const [selected, setSelected] = useState<InputOptions | undefined>(() =>
    resolveOption(defaultValue),
  )

  // Stable serialised key of options so the sync effect only re-runs when
  // the options list actually changes in content, not just reference
  const optionsKey = useMemo(
    () => options.map((o) => String(o.value)).join(','),
    [options],
  )

  // Filter options based on search query
  const filteredOptions = useMemo(() => {
    if (!searchable || !searchQuery.trim()) return options

    const lowerCaseQuery = searchQuery.toLowerCase()

    return options.filter((option: InputOptions) => {
      const optionLabel = option.label.toLowerCase()
      const optionValue = String(option.value).toLowerCase()
      return (
        optionLabel.includes(lowerCaseQuery) ||
        optionValue.includes(lowerCaseQuery)
      )
    })
  }, [options, searchQuery, searchable])

  // To close the dropdown in outside click
  const ref = useRef<HTMLDivElement | null>(null)
  useClickOutsideAndEscape({
    ref,
    callback: () => {
      setIsVisible(false)
      setSearchQuery('')
    },
  })

  // Reset userSelectedRef when defaultValue changes so a new defaultValue
  // from the parent (e.g. loading edit form data) is always applied
  useEffect(() => {
    userSelectedRef.current = false
  }, [defaultValue])

  // to initially set default value in Hook Form, and re-sync when options load asynchronously
  // (e.g. options fetched from API after defaultValue is already set)
  useEffect(() => {
    if (options.length === 0) return
    // Skip if the user has already made a manual selection so we don't
    // overwrite their choice when the parent re-renders with a new options reference
    if (userSelectedRef.current) return
    const resolved = resolveOption(defaultValue)
    setSelected(resolved)
    setValue(name, resolved?.value as PathValue<T, Path<T>>)
  }, [defaultValue, optionsKey])

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isVisible && searchable && searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [isVisible, searchable])

  // Will handle the select field change and add the updated value to register
  const handleValueSelect = useCallback(
    (option: InputOptions) => {
      userSelectedRef.current = true
      setSelected(option)
      setValue(name, option?.value as PathValue<T, Path<T>>, {
        shouldValidate: true,
      })
      // Notify the parent of a user-driven selection (not fired on default sync)
      onChange?.(option?.value)
      setIsVisible(false)
      setSearchQuery('')
    },
    [name, setValue, onChange],
  )

  //To handle rendering of Button or Checkbox in the dropdown
  const renderInput = useMemo(() => {
    if (!filteredOptions || filteredOptions.length === 0) {
      return (
        <div
          className={`block w-full rounded-md text-sm ${
            size === 'small' ? 'px-1.5 py-1' : 'px-4 py-2.5'
          } text-left text-gray-400`}
        >
          No results found
        </div>
      )
    }

    return filteredOptions.map((option: InputOptions) => (
      <button
        type="button"
        key={String(option.value)}
        value={String(option.value)}
        className={`block w-full cursor-pointer rounded-md text-sm ${
          size === 'small' ? 'px-1.5 py-1' : 'px-4 py-2.5'
        } text-left outline-none hover:bg-slate-100 disabled:cursor-not-allowed disabled:bg-transparent disabled:opacity-30`}
        onClick={() => handleValueSelect(option)}
        disabled={option?.disabled}
      >
        {option.label}
      </button>
    ))
  }, [filteredOptions, size, handleValueSelect])

  return (
    <div className={`relative w-full outline-none ${parentClassName}`}>
      {/* Label */}
      {label && (
        <label
          htmlFor={name}
          className="mb-1 text-xs font-semibold text-sidebar-primary"
        >
          {label}
          {required && <span className="text-error">*</span>}
        </label>
      )}
      {/* Select Button */}
      <div ref={ref}>
        <DropdownButton
          className={className}
          setIsVisible={setIsVisible}
          isVisible={isVisible}
        >
          {selected?.label ?? 'Select'}
        </DropdownButton>

        {/* Options */}
        <div
          className={`absolute z-10 h-max max-h-64 w-full min-w-max origin-top overflow-auto rounded-md bg-white shadow-lg transition-all duration-200 ${
            isVisible ? 'scale-y-100 opacity-100' : 'scale-y-0 opacity-0'
          }`}
        >
          {/* Search Input */}
          {searchable && options.length > 0 && (
            <div className="sticky top-0 border-b bg-white p-2">
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search..."
                className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm outline-none focus:border-gray-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          {/* Options List */}
          <div className="p-2">{renderInput}</div>
        </div>
      </div>

      {/* Error & Hint Text */}
      {(error || hintText) && (
        <div>
          {error ? (
            <p className="mt-1 text-xs text-error">{error}</p>
          ) : (
            <p className="mt-1 text-[11px] text-gray-500">{hintText}</p>
          )}
        </div>
      )}
    </div>
  )
}

export default Dropdown
